import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import Icon from './Icon';

interface HeaderProps {
  theme: string;
  setTheme: (theme: string) => void;
}

const Header: React.FC<HeaderProps> = ({ theme, setTheme }) => {
  const [isCopied, setIsCopied] = useState(false);
  
  const lightLogoUrl = "https://enfitah.com/wp-content/uploads/2025/01/enfitah-logo-01.svg";
  const darkLogoUrl = "https://enfitah.com/wp-content/uploads/2025/09/Enfitah-white-logo-01.svg";

  const handleShare = () => {
    if (isCopied) return; // Prevent multiple clicks while notification is showing
    navigator.clipboard.writeText(window.location.href).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(err => {
        console.error('Failed to copy link: ', err);
    });
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const navLinkClasses = 'text-sm font-medium text-[var(--color-text-body)] hover:text-[var(--color-primary)] transition-colors px-3 py-2 rounded-md';
  const activeNavLinkClasses = 'bg-[var(--color-primary)] text-white hover:text-white';

  return (
    <header className="sticky top-0 z-50 bg-[var(--color-surface)]/80 backdrop-blur-sm border-b border-[var(--color-border)]">
      <div className="flex items-center justify-between px-4 sm:px-6 lg:px-[70px] h-16">
        {/* Right Section (Logo & Title) */}
        <div className="flex items-center gap-x-3">
          <a href="https://enfitah.com/" target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={theme === 'dark' ? darkLogoUrl : lightLogoUrl}
              alt="Enfitah Logo"
              className="h-8 w-28 object-contain transition-transform hover:scale-105"
            />
          </a>
          <div className="w-px h-6 bg-gray-300/70 dark:bg-gray-600/70 hidden sm:block"></div>
          <span className="text-sm font-medium text-[var(--color-text-body)] tracking-wide hidden md:block">
            منشئ الصفحات القانونية
          </span>
        </div>

        {/* Left Section (Nav & Actions) */}
        <div className="flex items-center gap-x-2">
          <nav className="hidden md:flex items-center gap-x-2">
            <NavLink
              to="/"
              className={({ isActive }) => `${navLinkClasses} ${isActive ? activeNavLinkClasses : ''}`}
            >
              الرئيسية
            </NavLink>
            <NavLink
              to="/my-pages"
              className={({ isActive }) => `${navLinkClasses} ${isActive ? activeNavLinkClasses : ''}`}
            >
              صفحاتي
            </NavLink>
            <NavLink
              to="/changelog"
              className={({ isActive }) => `${navLinkClasses} ${isActive ? activeNavLinkClasses : ''}`}
            >
              سجل التغييرات
            </NavLink>
          </nav>

          <div className="flex items-center gap-x-2">
            <div className="relative">
              <button
                onClick={handleShare}
                className="h-10 w-10 flex items-center justify-center rounded-full text-[var(--color-text-body)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors"
                title="مشاركة الصفحة"
                aria-label="Share page"
              >
                <Icon name="Share2" className="w-5 h-5" />
              </button>
              {/* Notification Toast */}
              <div 
                className={`absolute top-full mt-6 right-1/2 translate-x-1/2 z-10 px-3 py-1.5 rounded-md shadow-lg bg-[#e0f5dc] text-[#0f3d04] border border-green-200/80 text-xs font-normal whitespace-nowrap transition-all duration-300 ease-in-out ${
                  isCopied ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
                }`}
              >
                تم نسخ الرابط!
              </div>
            </div>

            <button
              onClick={toggleTheme}
              className="h-10 w-10 flex items-center justify-center rounded-full text-[var(--color-text-body)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors"
              aria-label="Toggle theme"
              title={theme === 'light' ? 'التبديل إلى الوضع المظلم' : 'التبديل إلى الوضع الفاتح'}
            >
              {theme === 'light' ? (
                <Icon name="Moon" className="h-5 w-5" />
              ) : (
                <Icon name="Sun" className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;